// Vendors
import React, { useMemo, useCallback, useEffect } from 'react'
import { Link, useHistory } from 'react-router-dom'

// Functions
import { useTranslation } from 'react-i18next'
import { useListRecords } from 'hooks/useListRecords'

// Components
import { ListTable } from 'components/ListTable'
import { ListFilters } from 'components/ListFilters'
import { ListActions } from 'components/ListActions'
import { ListPagination } from 'components/ListPagination'
import { ListTableRenderAs } from 'components/ListTableRowComponent'

// Styles
import { LayoutLoggedPageList } from 'layout/PrivatePageList'
import { Button, Stack } from '@chakra-ui/react'
import { FiPlus } from 'react-icons/fi'

// Types
import { Seller } from './types'

import { PageMetaSetting } from './meta'
import { kebabCase } from 'lodash'

export const PageSellerListRecords = (): JSX.Element => {
  /*
  |-----------------------------------------------------------------------------
  | Constants
  |-----------------------------------------------------------------------------
  |
  |
  */
  const { t } = useTranslation('seller')
  const history = useHistory()
  const { endpoint, title } = PageMetaSetting
  const { records, query, pagination } = useListRecords<Seller>({
    endpoint,
    query: {
      type: 'seller',
    },
  })

  /*
  |-----------------------------------------------------------------------------
  | Effects.
  |-----------------------------------------------------------------------------
  |
  |
  */

  useEffect(() => {
    records.list.forEach((record) => {
      const rolesFormatted = record.role?.split(',').map((role) => {
        return t(`common:roles.${kebabCase(role)}`)
      })

      if (!rolesFormatted?.length) {
        record.rolesFormatted = 'Usuário não tem nem um perfil ativo'
      } else {
        record.rolesFormatted = rolesFormatted.join(', ')
      }
    })
  }, [records.list, t])

  /*
  |-----------------------------------------------------------------------------
  | Memos.
  |-----------------------------------------------------------------------------
  |
  |
  */

  const listTableHeaders = useMemo(
    () => [
      {
        label: t('table.headers.name'),
        accessor: 'name',
      },
      {
        label: t('table.headers.phone'),
        accessor: 'phoneNumber',
      },
      {
        label: t('table.headers.email'),
        accessor: 'email',
      },
      {
        label: t('table.headers.profile'),
        accessor: 'rolesFormatted',
        render: {
          as: ListTableRenderAs.MULTILINE_TEXT,
        },
      },
      {
        label: t('table.headers.status'),
        accessor: 'status',
        render: {
          as: ListTableRenderAs.BADGE,
        },
      },
    ],
    [t]
  )

  const listTableRowAction = useCallback(
    (record: Seller) => {
      if (!record) return
      history.push(`/sellers/${record.id}`)
    },
    [history]
  )

  /*
  |-----------------------------------------------------------------------------
  | Renders.
  |-----------------------------------------------------------------------------
  |
  |
  */

  return (
    <LayoutLoggedPageList title={title}>
      <Stack
        spacing="4"
        justify="space-between"
        direction={{ base: 'column', md: 'row' }}
      >
        <ListFilters query={query}></ListFilters>

        <ListActions>
          <Button as={Link} to={'/sellers/new'} leftIcon={<FiPlus />}>
            Adicionar
          </Button>
        </ListActions>
      </Stack>

      <ListTable
        records={records}
        headers={listTableHeaders}
        rowAction={listTableRowAction}
      />

      <ListPagination pagination={pagination} query={query} />
    </LayoutLoggedPageList>
  )
}
